import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Trash2,
  DollarSign,
  Loader,
  CheckCircle,
  AlertCircle,
  Info,
  TrendingUp,
  Zap,
  Award,
} from "lucide-react";
import useUserData from "../../../Hooks/useUserData";
import { ref, push, serverTimestamp, update } from "../../../Config/matuRtdb";
import { database } from "../../../Config/firebase";

// Componente de notificación personalizado
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 2000); // Reducido a 2 segundos

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      className="fixed top-4 right-4 p-3 rounded-lg shadow-lg flex items-center z-50"
      style={{
        background:
          type === "success"
            ? "linear-gradient(to right, #10B981, #059669)"
            : "linear-gradient(to right, #EF4444, #B91C1C)",
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      {type === "success" ? (
        <CheckCircle className="w-5 h-5 text-white mr-2" />
      ) : (
        <AlertCircle className="w-5 h-5 text-white mr-2" />
      )}
      <p className="text-white text-sm font-medium">{message}</p>
    </motion.div>
  );
};

// Proveedor de notificaciones
const useNotification = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type) => {
    setNotification({ message, type });
  };

  const hideNotification = () => {
    setNotification(null);
  };

  return {
    notification,
    showSuccess: (message) => showNotification(message, "success"),
    showError: (message) => showNotification(message, "error"),
    hideNotification,
  };
};

const BetModal = ({
  show,
  onClose,
  selectedBets,
  removeBet,
  calculateCombinedOdds,
  betAmount,
  setBetAmount,
  onSubmit,
  toggleFloatingButton,
}) => {
  const { userData, refreshUserData } = useUserData();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [userId, setUserId] = useState(null);
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();
  const [suggestedAmounts, setSuggestedAmounts] = useState([]);
  const [showPromotions, setShowPromotions] = useState(false);

  // Mínimo de apuesta establecido en 500 COP
  const MIN_BET_AMOUNT = 500;

  const combinedOdds = calculateCombinedOdds();
  const possibleWinnings = betAmount
    ? parseFloat(betAmount) * parseFloat(combinedOdds)
    : 0;
  const userBalance = userData?.saldo ? parseFloat(userData.saldo) : 0;
  const insufficientFunds = betAmount
    ? parseFloat(betAmount) > userBalance
    : false;
  const belowMinBet = betAmount
    ? parseFloat(betAmount) < MIN_BET_AMOUNT
    : false;

  // Generar montos sugeridos basados en el saldo del usuario
  useEffect(() => {
    if (userData && userData.saldo) {
      const balance = parseFloat(userData.saldo);
      // Crear sugerencias escalonadas basadas en el saldo, respetando el mínimo de 500 COP
      const suggestions = [
        Math.max(MIN_BET_AMOUNT, Math.round(balance * 0.05)),
        Math.round(balance * 0.1),
        Math.round(balance * 0.2),
      ].filter((amount) => amount <= balance);

      setSuggestedAmounts(suggestions);
    }
  }, [userData]);

  // Efecto mejorado para gestionar la visibilidad del botón flotante
  useEffect(() => {
    if (typeof toggleFloatingButton === "function") {
      // Siempre ocultar el botón cuando el modal está abierto
      if (show) {
        toggleFloatingButton(false);
      } else {
        // Mostrar el botón solo si hay apuestas seleccionadas al cerrar
        toggleFloatingButton(selectedBets && selectedBets.length > 0);
      }
    }
  }, [show, selectedBets, toggleFloatingButton]);

  // Mostrar promociones aleatorias ocasionalmente
  useEffect(() => {
    if (show && selectedBets.length > 0 && Math.random() > 0.7) {
      setTimeout(() => setShowPromotions(true), 800);
    } else {
      setShowPromotions(false);
    }
  }, [show, selectedBets]);

  // Obtener el ID del usuario desde localStorage
  useEffect(() => {
    try {
      const userUUID = localStorage.getItem("userUUID");
      if (userUUID) {
        setUserId(userUUID);
      } else {
        console.error("No se encontró el ID de usuario en localStorage");
        setError(
          "No se pudo identificar al usuario. Por favor, vuelva a iniciar sesión."
        );
      }
    } catch (err) {
      console.error("Error al obtener el ID de usuario:", err);
      setError("Error al verificar la sesión del usuario");
    }
  }, []);

  const handleSubmitBet = async () => {
    // Validaciones
    if (!userData || !userData.saldo) {
      setError("No se pudo obtener el saldo del usuario");
      return;
    }

    if (!userId) {
      setError("No se pudo identificar al usuario");
      return;
    }

    if (insufficientFunds) {
      setError("Saldo insuficiente para realizar esta apuesta");
      return;
    }

    if (belowMinBet) {
      setError(`La apuesta mínima es de ${MIN_BET_AMOUNT} COP`);
      return;
    }

    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      // Calcular nuevo saldo después de la apuesta
      const newBalance = userBalance - parseFloat(betAmount);

      // Crear objeto de apuesta con más datos
      const betData = {
        userId: userId,
        bets: selectedBets,
        amount: parseFloat(betAmount),
        odds: parseFloat(combinedOdds),
        possibleWinnings: possibleWinnings,
        status: "pendiente",
        createdAt: serverTimestamp(),
        betType: selectedBets.length > 1 ? "combinada" : "simple",
        estimatedPayout: possibleWinnings.toFixed(2),
      };

      // Guardar apuesta en la base de datos
      const userBettingsRef = ref(database, `usuarios/${userId}/bettings`);
      const newBetRef = await push(userBettingsRef, betData);

      // Actualizar el saldo del usuario
      const userRef = ref(database, `usuarios/${userId}`);
      await update(userRef, {
        saldo: newBalance.toFixed(2),
      });

      // Refrescar los datos del usuario
      if (refreshUserData) {
        refreshUserData();
      }

      // Mensaje personalizado según el tipo de apuesta - solo establecer en el estado interno
      const successMsg =
        selectedBets.length > 1
          ? "¡Apuesta combinada realizada con éxito!"
          : "¡Apuesta realizada con éxito!";

      setSuccessMessage(successMsg);
      // Eliminamos la llamada a showSuccess para evitar mostrar notificaciones de tipo alerta
      // showSuccess(successMsg);

      // Cerrar más rápidamente: solo 800ms de espera
      setTimeout(() => {
        if (onSubmit) {
          onSubmit();
        }
        setIsLoading(false);
        onClose();
      }, 800);
    } catch (error) {
      console.error("Error al realizar la apuesta:", error);
      setError("Error al procesar la apuesta. Inténtalo de nuevo.");
      showError("Error al procesar la apuesta");
      setIsLoading(false);
    }
  };

  // Función para seleccionar un monto sugerido
  const selectSuggestedAmount = (amount) => {
    setBetAmount(amount.toString());
  };

  return (
    <>
      {/* Sistema de notificaciones - solo para errores ahora */}
      <AnimatePresence>
        {notification && notification.type === "error" && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={hideNotification}
          />
        )}
      </AnimatePresence>

      {/* Modal de apuestas */}
      <AnimatePresence>
        {show && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 flex items-end justify-center z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <motion.div
              className="bg-gray-900 rounded-t-xl p-4 w-full max-w-md"
              style={{
                backgroundImage: "linear-gradient(to bottom, #111827, #1F2937)",
                boxShadow: "0 -8px 25px rgba(0,0,0,0.3)",
              }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cabecera del modal */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-medium flex items-center">
                  {selectedBets.length > 1 ? (
                    <>
                      <Zap className="w-5 h-5 mr-2 text-yellow-500" />
                      Apuesta Combinada
                    </>
                  ) : (
                    <>
                      <Award className="w-5 h-5 mr-2 text-yellow-500" />
                      Realizar Apuesta
                    </>
                  )}
                </h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Lista de apuestas seleccionadas */}
              <div className="max-h-64 overflow-y-auto mb-4">
                {selectedBets.map((bet, index) => (
                  <BetCard
                    key={index}
                    bet={bet}
                    onRemove={removeBet}
                    disabled={isLoading}
                  />
                ))}
              </div>

              {/* Cuota combinada (sólo para apuestas múltiples) */}
              {selectedBets.length > 1 && (
                <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-gray-700">
                  <div className="flex justify-between items-center">
                    <p className="text-white text-sm flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2 text-yellow-500" />
                      Cuota combinada
                    </p>
                    <p className="text-yellow-500 font-medium text-lg">
                      {combinedOdds}
                    </p>
                  </div>
                </div>
              )}

              {/* Promociones (aparecen aleatoriamente) */}
              <AnimatePresence>
                {showPromotions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 overflow-hidden"
                  >
                    <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3">
                      <p className="text-yellow-500 text-xs flex items-center mb-1">
                        <Info className="w-4 h-4 mr-1" />
                        Promoción disponible
                      </p>
                      <p className="text-white text-sm">
                        {selectedBets.length > 1
                          ? "¡Gana un 5% extra en apuestas combinadas de 3 o más eventos!"
                          : "¡Haz tu primera apuesta sin riesgo hasta $50.000 COP!"}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Campo de entrada para el monto de la apuesta */}
              <div className="mb-4">
                <div className="flex justify-between">
                  <label className="block text-gray-400 text-xs mb-1">
                    Valor de la apuesta (COP)
                  </label>
                  {userData && (
                    <p className="text-gray-400 text-xs mb-1 flex items-center">
                      Saldo:{" "}
                      <span className="text-white ml-1">
                        $ {parseFloat(userData.saldo).toLocaleString("es-CO")}{" "}
                        COP
                      </span>
                    </p>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    className={`bg-gray-800 rounded-lg w-full pl-10 pr-4 py-3 text-white focus:outline-none focus:ring-2 transition-all ${
                      insufficientFunds || belowMinBet
                        ? "focus:ring-red-500 border border-red-500"
                        : "focus:ring-yellow-500 border border-gray-700"
                    }`}
                    placeholder={`Mínimo ${MIN_BET_AMOUNT} COP`}
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
                {insufficientFunds && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Saldo insuficiente para realizar esta apuesta
                  </p>
                )}
                {belowMinBet && !insufficientFunds && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    La apuesta mínima es de {MIN_BET_AMOUNT} COP
                  </p>
                )}

                {/* Montos sugeridos */}
                {suggestedAmounts.length > 0 && (
                  <div className="flex space-x-2 mt-2 flex-wrap">
                    {suggestedAmounts.map((amount, index) => (
                      <button
                        key={index}
                        onClick={() => selectSuggestedAmount(amount)}
                        className="bg-gray-800 hover:bg-gray-700 text-white text-xs py-1 px-3 rounded-full border border-gray-700 transition-colors mb-1"
                        disabled={isLoading}
                      >
                        ${amount.toLocaleString("es-CO")}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Resumen de la apuesta */}
              {betAmount && !isNaN(betAmount) && parseFloat(betAmount) > 0 && (
                <BetSummary
                  betAmount={betAmount}
                  combinedOdds={combinedOdds}
                  numBets={selectedBets.length}
                />
              )}

              {/* Mensajes de error */}
              {error && (
                <div className="mb-4 p-2 bg-red-900/30 border border-red-500 rounded-lg">
                  <p className="text-red-500 text-xs flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {error}
                  </p>
                </div>
              )}

              {/* Mensajes de éxito */}
              {successMessage && (
                <div className="mb-4 p-2 bg-green-900/30 border border-green-500 rounded-lg">
                  <p className="text-green-500 text-xs flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {successMessage}
                  </p>
                </div>
              )}

              {/* Botón de envío */}
              <motion.button
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-medium py-3 px-4 rounded-lg w-full relative overflow-hidden"
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmitBet}
                disabled={
                  isLoading ||
                  !betAmount ||
                  isNaN(betAmount) ||
                  parseFloat(betAmount) < MIN_BET_AMOUNT ||
                  insufficientFunds
                }
              >
                {/* Efecto de carga en el botón */}
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Procesando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    {selectedBets.length > 1 ? (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        Realizar Apuesta Combinada
                      </>
                    ) : (
                      <>
                        <DollarSign className="w-5 h-5 mr-2" />
                        Realizar Apuesta
                      </>
                    )}
                  </span>
                )}

                {/* Overlay para botón deshabilitado */}
                {(isLoading ||
                  !betAmount ||
                  isNaN(betAmount) ||
                  parseFloat(betAmount) < MIN_BET_AMOUNT ||
                  insufficientFunds) && (
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                )}
              </motion.button>

              {/* Texto informativo */}
              {!error && !successMessage && (
                <p className="text-gray-500 text-xs text-center mt-3">
                  Al confirmar, aceptas los términos y condiciones de apuestas
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Component for each bet card in the modal
const BetCard = ({ bet, onRemove, disabled }) => {
  return (
    <div className="bg-gray-800 rounded-lg p-3 mb-2 border border-gray-700 relative overflow-hidden">
      {/* Indicador de partido destacado */}
      {bet.featured && (
        <div className="absolute top-0 right-0">
          <div className="bg-yellow-500 text-black text-xs py-1 px-2 rounded-bl-lg font-medium">
            Destacado
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-white text-sm mb-1 font-medium">
          {bet.homeTeam} vs {bet.awayTeam}
        </p>
        <button
          onClick={() => onRemove(bet.matchId)}
          className="text-gray-400 hover:text-red-500 transition-colors"
          disabled={disabled}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex justify-between items-center mt-1">
        <div>
          <p className="text-gray-400 text-xs">Apuesta a</p>
          <p className="text-white text-sm">{bet.type}</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs text-right">Cuota</p>
          <p className="text-yellow-500 font-medium text-right text-lg">
            {bet.odd}
          </p>
        </div>
      </div>

      {/* Fecha/hora del partido */}
      {bet.matchTime && (
        <p className="text-gray-500 text-xs mt-2">{bet.matchTime}</p>
      )}
    </div>
  );
};

// Component for bet summary
const BetSummary = ({ betAmount, combinedOdds, numBets }) => {
  const amount = parseFloat(betAmount);
  const winnings = amount * parseFloat(combinedOdds);
  const profit = winnings - amount;

  // Calcular bono para apuestas combinadas (simulado)
  const hasBonus = numBets >= 3;
  const bonusAmount = hasBonus ? winnings * 0.05 : 0;
  const totalWinnings = winnings + bonusAmount;

  return (
    <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-gray-700">
      <div className="flex justify-between mb-1">
        <p className="text-gray-400 text-xs">Tu apuesta</p>
        <p className="text-white text-sm">
          $ {amount.toLocaleString("es-CO")} COP
        </p>
      </div>

      <div className="flex justify-between mb-1">
        <p className="text-gray-400 text-xs">Beneficio potencial</p>
        <p className="text-green-500 text-sm">
          +$ {profit.toLocaleString("es-CO")} COP
        </p>
      </div>

      {hasBonus && (
        <div className="flex justify-between mb-1">
          <p className="text-yellow-500 text-xs flex items-center">
            <Zap className="w-3 h-3 mr-1" />
            Bono combinada 5%
          </p>
          <p className="text-yellow-500 text-xs">
            +$ {bonusAmount.toLocaleString("es-CO")} COP
          </p>
        </div>
      )}

      <div className="border-t border-gray-700 mt-2 pt-2 flex justify-between">
        <p className="text-gray-200 text-sm font-medium">Ganancia total</p>
        <p className="text-green-400 text-sm font-bold">
          $ {totalWinnings.toLocaleString("es-CO")} COP
        </p>
      </div>
    </div>
  );
};

export default BetModal;
